import { useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import * as savedService from '../services/savedService';
import { hashToNumericId } from '../utils/adToListing';

export function useSavedItems() {
  const [savedItems, setSavedItems] = useState<number[]>([]);
  const { isAuthenticated } = useAuth();

  // Initialize from API when authenticated
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isAuthenticated) return;
      try {
        const data = await savedService.listSavedItems();
        if (cancelled) return;
        const nums = data.items.map(({ ad }) => hashToNumericId(ad.id));
        setSavedItems(nums);
      } catch {
        // swallow; keep local state
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const toggleSave = useCallback(async (id: number, uuid?: string): Promise<void> => {
    // Optimistic UI
    setSavedItems((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);

    if (!isAuthenticated || !uuid) return;

    try {
      const res = await savedService.toggleSaved(uuid);
      const numeric = hashToNumericId(res.ad_id || uuid);
      setSavedItems((prev) => {
        const has = prev.includes(numeric);
        if (res.saved && !has) return [...prev, numeric];
        if (!res.saved && has) return prev.filter((i) => i !== numeric);
        return prev;
      });
    } catch {
      // revert optimistic change on error
      setSavedItems((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);
    }
  }, [isAuthenticated]);

  const isSaved = useCallback(
    (id: number): boolean => savedItems.includes(id),
    [savedItems]
  );

  return { savedItems, toggleSave, isSaved };
}
